import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import RULWarrantyDrillDownModal from "./RULWarranty/RULWarrantyDrillDownModal";
import RULWarrantyTreemap from "../../../Charts/Treemap/RULWarrantyTreemap";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import { getWarrantyConfigs, getWarrantyBucketCounts } from "../../../Api/BatteryHealthApi";
import CODE from "../../../Static/Constants/StatusCodes";

const CARD_STYLE_BY_KEY = {
  predictedRulLess: {
    title: "Predicted Life < Warranty",
    color: "#FC8B43",
  },
  expiredWarranty: {
    title: "Expired Warranty",
    color: "#B52824",
  },
  reachedEolBefore: {
    title: "Reached EOL before Warranty",
    color: "#E94E46",
  },
  predictedRulGreater: {
    title: "Predicted Life > Warranty",
    color: "#9BB938",
  },
};

// For each key, the label the backend expects for the WarrantyBucket filter
const CARD_KEY_TO_API_LABEL = Object.fromEntries(
  Object.entries(CARD_STYLE_BY_KEY).map(([key, val]) => [key, val.apiLabel ?? val.title])
);

// Map API xLabel → card key (use apiLabel if present, otherwise fall back to title)
const BUCKET_LABEL_TO_CARD_KEY = Object.fromEntries(
  Object.entries(CARD_STYLE_BY_KEY).map(([key, val]) => [val.apiLabel ?? val.title, key])
);

const EMPTY_COUNTS = {
  predictedRulLess: 0,
  expiredWarranty: 0,
  reachedEolBefore: 0,
  predictedRulGreater: 0,
};

function formatConditionLogic(logic) {
  if (logic === "any_breached") return "Any condition breached";
  if (logic === "all_breached") return "All conditions breached";
  return logic;
}

const RULWarrantyOverview = ({ globalFilters = {} }) => {
  const { baseEntityTypePluralLable } = useBaseEntityInfo();

  const [configList, setConfigList] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [selectedConfigName, setSelectedConfigName] = useState(null);
  const [warrantyTerms, setWarrantyTerms] = useState(null);
  const [cardCounts, setCardCounts] = useState(EMPTY_COUNTS);
  const [countsLoading, setCountsLoading] = useState(false);

  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownDataPoint, setDrillDownDataPoint] = useState(null);

  // Fetch warranty config list when globalFilters change
  useEffect(() => {
    let active = true;
    setConfigsLoading(true);
    getWarrantyConfigs(globalFilters)
      .then((res) => {
        if (!active) return;
        const list = res?.responseStatus?.code === CODE.SUCCESS
          ? (Array.isArray(res?.response?.data) ? res.response.data : [])
          : [];
        setConfigList(list);
        if (list.length > 0) {
          setSelectedConfigName(list[0].configName);
          setWarrantyTerms(list[0]);
        } else {
          setSelectedConfigName(null);
          setWarrantyTerms(null);
        }
      })
      .catch(() => {
        if (active) {
          setConfigList([]);
          setSelectedConfigName(null);
          setWarrantyTerms(null);
        }
      })
      .finally(() => {
        if (active) setConfigsLoading(false);
      });
    return () => { active = false; };
  }, [globalFilters]);

  // Fetch bucket counts when selected config or global filters change
  useEffect(() => {
    if (!selectedConfigName) return;
    let active = true;
    setCountsLoading(true);
    getWarrantyBucketCounts(selectedConfigName, globalFilters)
      .then((res) => {
        if (!active) return;
        const data = res?.responseStatus?.code === CODE.SUCCESS
          ? (Array.isArray(res?.response?.data) ? res.response.data : [])
          : [];
        const counts = { ...EMPTY_COUNTS };
        data.forEach((item) => {
          const key = BUCKET_LABEL_TO_CARD_KEY[item.xLabel];
          if (key) {
            const metricItem = (item.metricsArray || []).find(
              (m) => m.type === "DistinctVehicleCount"
            );
            counts[key] = metricItem?.value ?? 0;
          }
        });
        setCardCounts(counts);
      })
      .catch(() => {
        if (active) setCardCounts(EMPTY_COUNTS);
      })
      .finally(() => {
        if (active) setCountsLoading(false);
      });
    return () => { active = false; };
  }, [selectedConfigName, globalFilters]);

  const handleConfigSelect = useCallback((config) => {
    setSelectedConfigName(config.configName);
    setWarrantyTerms(config);
  }, []);

  const treemapData = useMemo(
    () =>
      Object.keys(CARD_STYLE_BY_KEY).map((key) => ({
        key,
        title: CARD_STYLE_BY_KEY[key].title,
        color: CARD_STYLE_BY_KEY[key].color,
        value: cardCounts[key] ?? 0,
      })),
    [cardCounts]
  );

  const warrantyTermsDisplay = useMemo(() => {
    if (!warrantyTerms) return [];
    const parts = [];
    if (warrantyTerms.maxCycles != null)
      parts.push(`Max Cycles: ${warrantyTerms.maxCycles.toLocaleString()}`);
    if (warrantyTerms.maxYears != null)
      parts.push(`Max Years: ${warrantyTerms.maxYears}`);
    if (warrantyTerms.maxKm != null)
      parts.push(`Max Km: ${warrantyTerms.maxKm.toLocaleString()}`);
    return parts;
  }, [warrantyTerms]);

  const handleCellClick = useCallback((item) => {
    setDrillDownDataPoint({
      cardKey: item.key,
      cardTitle: item.title,                          // display label (shown in modal header)
      warrantyBucket: CARD_KEY_TO_API_LABEL[item.key], // API label (used as WarrantyBucket filter)
    });
    setDrillDownOpen(true);
  }, []);

  if (configsLoading) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          p: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (configList.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          p: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#686868", fontSize: "14px" }}>
          No warranty conditions configured for this organization.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          maxWidth: "calc(100vw - 102px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "28px",
            py: "24px",
          }}
        >
          <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
            RUL vs. Warranty
          </Typography>
        </Box>

        <Box sx={{ display: "flex", px: "28px", pb: "28px" }}>
          {/* Left panel — config list */}
          <Box sx={{ width: 300, pr: 2, flexShrink: 0 }}>
            <Typography variant="fw400s12c686868" sx={{ color: "#686868", mb: 1 }}>
              SELECT CONFIGURATION
            </Typography>
            <Divider sx={{ borderColor: "#E4E4E4", mb: 1.25 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {configList.map((option) => {
                const isSelected = selectedConfigName === option.configName;
                return (
                  <Box
                    key={option.configName}
                    onClick={() => handleConfigSelect(option)}
                    sx={{
                      px: "16px",
                      py: "10px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#EEF0FF" : "transparent",
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: isSelected ? "#EEF0FF" : "#F5F5F8" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: isSelected ? "#4C50D9" : "#2E2E2E",
                        fontWeight: isSelected ? 600 : 500,
                      }}
                    >
                      {option.configName}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "#E4E4E4", ml: "0px", mr: "10px" }}
          />

          {/* Right panel — warranty condition + treemap */}
          <Box sx={{ flex: 1, minWidth: 0, pl: 1 }}>
            <Box
              sx={{
                border: "1px solid #C9CEF5",
                borderRadius: "8px",
                backgroundColor: "#F2F3FF",
                px: "18px",
                py: "14px",
                mb: "16px",
              }}
            >
              <Typography
                sx={{
                  color: "#4C50D9",
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: 600,
                  mb: 0.75,
                }}
              >
                Warranty Condition
              </Typography>

              {warrantyTermsDisplay.length > 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                  {warrantyTermsDisplay.map((term, i) => (
                    <React.Fragment key={term}>
                      {i > 0 && (
                        <Typography sx={{ color: "#C9C9C9", fontSize: "16px", lineHeight: "24px" }}>
                          |
                        </Typography>
                      )}
                      <Typography sx={{ color: "#2E2E2E", fontSize: "16px", lineHeight: "24px" }}>
                        {term}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ color: "#686868", fontSize: "14px" }}>
                  No warranty terms available
                </Typography>
              )}

              {warrantyTerms?.conditionLogic && (
                <Typography sx={{ color: "#686868", fontSize: "13px", mt: 0.5 }}>
                  Logic: {formatConditionLogic(warrantyTerms.conditionLogic)}
                </Typography>
              )}
            </Box>

            <RULWarrantyTreemap
              data={treemapData}
              loading={countsLoading}
              onCellClick={handleCellClick}
              selectedConfigName={selectedConfigName}
              baseEntityTypePluralLable={baseEntityTypePluralLable}
              height={374}
            />
          </Box>
        </Box>
      </Box>

      <RULWarrantyDrillDownModal
        open={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        selectedDataPoint={drillDownDataPoint}
        configName={selectedConfigName}
        globalFilters={globalFilters}
      />
    </>
  );
};

export default RULWarrantyOverview;
